import React from "react";
import { Badge } from "flowbite-react";
import Chart from "react-apexcharts";

/**
 * Componente: LiquidezRecebimentoPJQuery (valor único)
 * Mostra a % de boletos RECEBIDOS (quitados) sobre os EMITIDOS nos últimos 12 meses.
 *
 * Props:
 * - data?: { indicador?: number | string | null } // 0..1 (ex: 0.87 => 87%)
 * - periodLabel?: string (default: "Últimos 12 meses")
 * - className?: string
 * - layout?: "full" | "half"  // "half" quando estiver em 2 colunas
 */
export default function LiquidezRecebimentoPJQuery({
  data,
  periodLabel = "Últimos 12 meses",
  className = "",
  layout = "full",
}) {
  // Sem dados suficientes
  if (data?.indicador == null) {
    return (
      <div>
        <div
          className={`w-full rounded-md border border-gray-200 p-5 mt-4 ${className}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Saúde Financeira - Recebimentos (Boleto)
              </h3>
              <p className="mt-0.5 text-sm text-gray-500">
                {periodLabel
                  ? `Período: ${periodLabel}`
                  : "Indicador indisponível"}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mt-4">
            <div className="flex gap-3">
              <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
              <div className="text-sm leading-relaxed text-amber-900">
                Não há informações suficientes para exibir este indicador.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isHalf = layout === "half"; // duas colunas -> orientação vertical/compacta

  // Cálculos simples
  const raw = Number(data?.indicador);
  const v = Number.isFinite(raw) ? clamp(raw, 0, 1) : 0; // 0..1
  const pct = Math.round(v * 100); // 1 -> 100%

  const tier = getTier(v);
  const { colorHex } = tierColors(tier);

  // Gauge radial (valor único)
  const radialSeries = [pct];
  const radialOptions = {
    chart: {
      type: "radialBar",
      toolbar: { show: false },
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        startAngle: -110,
        endAngle: 110,
        hollow: { size: isHalf ? "58%" : "66%" },
        track: { background: "#F3F4F6" }, // gray-100
        dataLabels: {
          name: {
            show: true,
            offsetY: isHalf ? 32 : 38, // rótulo abaixo do centro
            fontSize: isHalf ? "11px" : "12px",
            color: "#6B7280",
            formatter: () => "Liquidez",
          },
          value: {
            show: true,
            offsetY: isHalf ? -10 : -12, // valor acima do centro
            fontSize: isHalf ? "28px" : "34px",
            fontWeight: 800,
            formatter: (val) => `${Math.round(val)}%`,
          },
        },
      },
    },
    labels: ["Liquidez"],
    colors: [colorHex],
    stroke: { lineCap: "round" },
    grid: { padding: { top: 8, bottom: 8 } },
    tooltip: { enabled: false },
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="mt-4 rounded-lg border border-gray-200 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Saúde Financeira - Recebimentos (Boleto)
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Percentual de boletos recebidos (quitados) sobre os boletos
              emitidos • {periodLabel}
            </p>
          </div>
          <Badge color={tierBadgeColor(tier)} className="capitalize">
            {tier}
          </Badge>
        </div>

        {/* Orientação muda conforme o layout:
            - full: gráfico à esquerda e texto à direita (horizontal)
            - half: gráfico em cima e texto embaixo (vertical/compacta) */}
        <div
          className={
            isHalf
              ? "mt-6 flex flex-col gap-4 items-stretch"
              : "mt-6 grid grid-cols-3 gap-8 items-center"
          }
        >
          {/* Gráfico */}
          <div className="flex items-center justify-center">
            <div
              className={
                isHalf ? "w-full max-w-[260px]" : "w-full max-w-[320px]"
              }
            >
              <Chart
                options={radialOptions}
                series={radialSeries}
                type="radialBar"
                width="100%"
              />
            </div>
          </div>

          {/* Texto contextual */}
          <div className={isHalf ? "" : "col-span-2"}>
            <ContextMessage tier={tier} pct={pct} compact={isHalf} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================= Mensagem contextual ======================= */
function ContextMessage({ tier, pct, compact = false }) {
  const content =
    tier === "excelente" ? (
      <p>
        Liquidez <strong>excelente</strong> ({pct}%). A adimplência dos{" "}
        <em>clientes</em> está em nível muito saudável.
      </p>
    ) : tier === "boa" ? (
      <p>
        Liquidez <strong>boa</strong> ({pct}%). Mantenha as políticas de
        cobrança e monitoramento.
      </p>
    ) : tier === "atenção" ? (
      <p>
        Liquidez em <strong>atenção</strong> ({pct}%). Reforce comunicações,
        prazos e condições para reduzir atrasos.
      </p>
    ) : (
      <p>
        Liquidez <strong>crítica</strong> ({pct}%). Sinaliza inadimplência
        elevada; avalie renegociação e ações de cobrança.
      </p>
    );

  return (
    <div
      className={`rounded-2xl border p-4 ${
        compact ? "text-sm" : ""
      } ${messageBorderClass(tier)}`}
    >
      <div className="flex gap-3">
        <span
          className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ${messageDotClass(
            tier
          )}`}
        />
        <div className="leading-relaxed text-gray-700">{content}</div>
      </div>
    </div>
  );
}

/* ======================= Helpers ======================= */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// Tiers sugeridos: >= 95% excelente, 85–94% boa, 70–84% atenção, < 70% crítica
function getTier(v /* 0..1 */) {
  const p = v * 100;
  if (p >= 95) return "excelente";
  if (p >= 85) return "boa";
  if (p >= 70) return "atenção";
  return "crítica";
}

function tierColors(tier) {
  switch (tier) {
    case "excelente":
      return { colorHex: "#22c55e" }; // green-500
    case "boa":
      return { colorHex: "#3b82f6" }; // blue-500
    case "atenção":
      return { colorHex: "#f59e0b" }; // amber-500
    case "crítica":
    default:
      return { colorHex: "#dc2626" }; // red-600
  }
}

function tierBadgeColor(tier) {
  return (
    {
      excelente: "success",
      boa: "info",
      atenção: "warning",
      crítica: "failure",
    }[tier] || "gray"
  );
}

function messageBorderClass(tier) {
  switch (tier) {
    case "excelente":
      return "border-green-200 bg-green-50";
    case "boa":
      return "border-blue-200 bg-blue-50";
    case "atenção":
      return "border-amber-200 bg-amber-50";
    case "crítica":
    default:
      return "border-red-200 bg-red-50";
  }
}

function messageDotClass(tier) {
  switch (tier) {
    case "excelente":
      return "bg-green-500";
    case "boa":
      return "bg-blue-500";
    case "atenção":
      return "bg-amber-500";
    case "crítica":
    default:
      return "bg-red-600";
  }
}
